"use client";

import { m as motion } from "framer-motion";
import { Section } from "@/components/ui/section";
import { BackgroundLines } from "@/components/ui/background-lines";
import About from "@/components/sections/About";
import Experience from "@/components/sections/Experience";
import Education from "@/components/sections/Education";
import Skills from "@/components/sections/Skills";
import { FAQ } from "@/components/sections/FAQ";
import { SectionDivider } from "@/components/ui/section-divider";
import { SEO_CONFIG } from "@/lib/seo-config";
import BlogFooter from "@/components/blog/BlogFooter";
import {
  MapPin,
  Briefcase,
  GraduationCap,
  Github,
  Linkedin,
  Twitter,
} from "lucide-react";

interface AboutPageClientProps {
  aboutData?: any;
  experienceData?: any;
  educationData?: any;
  skillsData?: any;
}

export default function AboutPageClient({
  aboutData,
  experienceData,
  educationData,
  skillsData,
}: AboutPageClientProps) {
  return (
    <main className="min-h-screen relative overflow-hidden">
      <div className="relative pt-20 md:pt-32 pb-12 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          {/* Hero Section - Entity Identity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-6 md:space-y-8 mb-12 md:mb-20"
          >
            <div className="flex justify-center mb-4 md:mb-6">
              <motion.img
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 }}
                src={SEO_CONFIG.avatar}
                alt={SEO_CONFIG.name}
                className="w-24 h-24 md:w-32 md:h-32 rounded-full border-4 border-primary/20 shadow-2xl contrast-[1.05] saturate-[1.2] brightness-[1.1] dark:brightness-[0.85] hover:saturate-[1.4] transition-all duration-500"
              />
            </div>

            <div className="space-y-4 md:space-y-6">
              <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter">
                {SEO_CONFIG.name}
              </h1>
              <p className="text-lg sm:text-xl md:text-3xl text-muted-foreground font-black uppercase tracking-[0.2em]">
                {SEO_CONFIG.role}
              </p>
            </div>

            {/* Identity Markers */}
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-muted-foreground text-sm md:text-base">
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <span>
                  {SEO_CONFIG.location.city}, {SEO_CONFIG.location.country}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Briefcase className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <span>{SEO_CONFIG.experience.companies[0]}</span>
              </div>
              <div className="flex items-center gap-2">
                <GraduationCap className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <span>{SEO_CONFIG.education.institution}</span>
              </div>
            </div>

            {/* Social Verification Links */}
            <div className="flex items-center justify-center gap-4 md:gap-6 pt-4 md:pt-6">
              <a
                href={SEO_CONFIG.social.github}
                target="_blank"
                rel="me noopener noreferrer"
                className="p-3 md:p-4 rounded-2xl bg-secondary hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-sm"
                aria-label="GitHub Profile"
              >
                <Github className="w-5 h-5 md:w-6 md:h-6" />
              </a>
              <a
                href={SEO_CONFIG.social.linkedin}
                target="_blank"
                rel="me noopener noreferrer"
                className="p-3 md:p-4 rounded-2xl bg-secondary hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-sm"
                aria-label="LinkedIn Profile"
              >
                <Linkedin className="w-5 h-5 md:w-6 md:h-6" />
              </a>
              <a
                href={SEO_CONFIG.social.twitter}
                target="_blank"
                rel="me noopener noreferrer"
                className="p-3 md:p-4 rounded-2xl bg-secondary hover:bg-primary hover:text-primary-foreground transition-all duration-300 shadow-sm"
                aria-label="Twitter Profile"
              >
                <Twitter className="w-5 h-5 md:w-6 md:h-6" />
              </a>
            </div>

            <p className="max-w-4xl mx-auto text-base sm:text-lg md:text-xl leading-relaxed text-muted-foreground/80 px-4 md:px-0 font-medium">
              {SEO_CONFIG.description}
            </p>
          </motion.div>

          <SectionDivider />

          {/* About Section */}
          <About data={aboutData} />

          <SectionDivider />

          {/* Experience Section */}
          <Experience data={experienceData} />

          <SectionDivider />

          {/* Education Section */}
          <Education data={educationData} />

          <SectionDivider />

          {/* Skills Section */}
          <Skills data={skillsData} />

          <SectionDivider />

          {/* Entity Verification Notice */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-20 text-center space-y-4"
          >
            <h2 className="text-2xl font-bold">Verify My Identity</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              All social profiles below link back to this website for entity
              verification:
            </p>
            <div className="flex flex-col items-center gap-2 text-sm">
              <a
                href={SEO_CONFIG.social.github}
                target="_blank"
                rel="me noopener noreferrer"
                className="text-primary hover:underline"
              >
                {SEO_CONFIG.social.github}
              </a>
              <a
                href={SEO_CONFIG.social.linkedin}
                target="_blank"
                rel="me noopener noreferrer"
                className="text-primary hover:underline"
              >
                {SEO_CONFIG.social.linkedin}
              </a>
              <a
                href={SEO_CONFIG.social.twitter}
                target="_blank"
                rel="me noopener noreferrer"
                className="text-primary hover:underline"
              >
                {SEO_CONFIG.social.twitter}
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      <SectionDivider />
      <FAQ />
      <BlogFooter />
    </main>
  );
}
